// src/services/dataService.js
import initialData from '../data/data.json'

// Helper function to sanitize objects for IPC transfer
function sanitizeForIPC(obj) {
  // Return a safe copy with only the properties we need
  if (!obj) return null;
  
  try {
    // Simple serialization/deserialization to strip any non-serializable properties
    return JSON.parse(JSON.stringify(obj));
  } catch (error) {
    console.error('Error sanitizing object for IPC:', error);
    // Fallback for objects that can't be serialized with JSON
    if (obj.id !== undefined) {
      return {
        id: obj.id,
        name: obj.name || '',
        region: obj.region || 0,
        contentType: obj.contentType || '',
        videoUrl: obj.videoUrl || null,
        captionsUrl: obj.captionsUrl || null,
        message: obj.message || null,
        thumbnail: obj.thumbnail || '',
        description: obj.description || '',
        location: obj.location || '' // Added the location field
      };
    }
    return null;
  }
}

export default {
  // Check if we're in Electron or browser environment
  isElectron() {
    return window && window.dbAPI;
  },
  
  // Get all regions
  getRegions() {
    if (this.isElectron()) {
      console.log('Getting regions from Electron DB');
      return window.dbAPI.getRegions();
    } else {
      console.log('Getting regions from browser storage');
      return this.getAllData().then(data => data.regions);
    }
  },
  
  // Get a specific region
  getRegion(id) {
    if (this.isElectron()) {
      console.log(`Getting region ${id} from Electron DB`);
      return window.dbAPI.getRegion(id);
    } else {
      console.log(`Getting region ${id} from browser storage`);
      return this.getAllData().then(data => {
        return data.regions.find(region => region.id === id);
      });
    }
  },
  
  // Get all people
  getPeople() {
    if (this.isElectron()) {
      console.log('Getting all people from Electron DB');
      return window.dbAPI.getPeople().then(people => {
        console.log('People data received from DB:', people);
        return people;
      });
    } else {
      console.log('Getting all people from browser storage');
      return this.getAllData().then(data => data.people);
    }
  },
  
  // Get people by region
  getPeopleByRegion(regionId) {
    if (this.isElectron()) {
      console.log(`Getting people for region ${regionId} from Electron DB`);
      return window.dbAPI.getPeopleByRegion(regionId).then(people => {
        console.log(`People data for region ${regionId}:`, people);
        return people;
      });
    } else {
      console.log(`Getting people for region ${regionId} from browser storage`);
      return this.getAllData().then(data => {
        return data.people.filter(person => person.region === regionId);
      });
    }
  },
  
  // Get a specific person
  getPerson(id) {
    if (this.isElectron()) {
      console.log(`Getting person ${id} from Electron DB`);
      return window.dbAPI.getPerson(id).then(person => {
        console.log(`Person ${id} data:`, person);
        return person;
      });
    } else {
      console.log(`Getting person ${id} from browser storage`);
      return this.getAllData().then(data => {
        return data.people.find(person => person.id === id);
      });
    }
  },
  
  // Update person data
  updatePerson(person) {
    if (this.isElectron()) {
      console.log(`Updating person ${person.id} in Electron DB:`, person);
      
      // Sanitize the person object before sending through IPC
      const sanitizedPerson = sanitizeForIPC(person);
      if (!sanitizedPerson) {
        console.error('Failed to sanitize person object for IPC transfer');
        return Promise.resolve(false);
      }
      
      return window.dbAPI.updatePerson(sanitizedPerson);
    } else {
      console.log(`Updating person ${person.id} in browser storage`);
      return this.getAllData().then(data => {
        const index = data.people.findIndex(p => p.id === person.id);
        if (index !== -1) {
          data.people[index] = person;
          localStorage.setItem('galleryData', JSON.stringify(data));
          return true;
        }
        return false;
      });
    }
  },
  
  // Upload a new video
  uploadVideo(personId, file) {
    if (this.isElectron()) {
      console.log(`Uploading video for person ${personId} in Electron`);
      // In Electron we would have access to the file's path
      return window.dbAPI.uploadVideo(personId, file.path || file.name);
    } else {
      console.log(`Simulating video upload for person ${personId} in browser`);
      // Fallback implementation for browser
      return this.getPerson(personId).then(person => {
        if (person && person.contentType === 'video') {
          const videoUrl = `/videos/${file.name}`;
          person.videoUrl = videoUrl;
          return this.updatePerson(person).then(() => videoUrl);
        }
        return null;
      });
    }
  },
  
  // Upload captions for a video
  uploadCaptions(personId, file) {
    console.log(`Uploading captions for person ${personId}`);
    
    if (this.isElectron()) {
      return window.dbAPI.uploadCaption(personId, file.path || file.name);
    } else {
      return this.getPerson(personId).then(person => {
        if (person && person.contentType === 'video') {
          const captionsUrl = `/captions/${file.name}`;
          person.captionsUrl = captionsUrl;
          return this.updatePerson(person).then(() => captionsUrl);
        }
        return null;
      });
    }
  },

  // Check database status (only works in Electron)
  checkDatabaseStatus() {
    if (this.isElectron()) {
      console.log('Checking database status');
      return window.dbAPI.getDatabaseStatus();
    } else {
      console.log('Database status check not available in browser');
      return Promise.resolve({
        connected: false,
        mode: "browser",
        message: "Running in browser mode - SQLite database not available"
      });
    }
  },
  
  // Browser fallback methods - using localStorage
  getAllData() {
    try {
      const data = localStorage.getItem('galleryData');
      if (data) {
        return Promise.resolve(JSON.parse(data));
      }
      
      // If no data in localStorage, use the imported data
      console.log('No data in localStorage, using initial data');
      localStorage.setItem('galleryData', JSON.stringify(initialData));
      return Promise.resolve(initialData);
    } catch (error) {
      console.error('Error getting data:', error);
      return Promise.resolve(null);
    }
  },

  // For thumbnail upload
  async uploadThumbnail(personId, file) {
    console.log(`Uploading thumbnail for person ${personId}`);
    
    if (this.isElectron()) {
      return window.dbAPI.uploadThumbnail(personId, file.path || file.name);
    } else {
      try {
        const person = await this.getPerson(personId);
        if (!person) return null;
        
        // Create a thumbnail URL
        const thumbnailUrl = `/thumbnails/${file.name}`;
        
        // Update the person with the new thumbnail URL
        person.thumbnail = thumbnailUrl;
        const success = await this.updatePerson(person);
        
        return success ? thumbnailUrl : null;
      } catch (error) {
        console.error('Error uploading thumbnail:', error);
        return null;
      }
    }
  }
}